// Improved formatters.js for EWS Analysis Results

/**
 * Attempts to parse JSON from a text string with robust error handling
 * @param {string} text - Text that might contain JSON
 * @returns {Object|null} Parsed JSON object or null if parsing fails
 */
export const tryParseJson = (text) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    console.error("JSON parse error:", error.message);
    return null;
  }
};

/**
 * Extracts JSON from code blocks in a larger text
 * @param {string} text - Text that might contain JSON in code blocks
 * @returns {Object|null} Extracted JSON object or null if extraction fails
 */
export const extractJsonFromCodeBlocks = (text) => {
  if (!text) return null;
  
  // Find JSON in code blocks with ```json markers
  const codeBlockRegex = /```json\s*([\s\S]*?)\s*```/g;
  let match = codeBlockRegex.exec(text);
  
  if (match && match[1]) {
    const jsonStr = match[1].trim();
    const parsed = tryParseJson(jsonStr);
    if (parsed !== null) {
      return parsed;
    }
  }
  
  // If no JSON code blocks found, look for generic code blocks
  const genericBlockRegex = /```\s*([\s\S]*?)\s*```/g;
  match = genericBlockRegex.exec(text);
  
  if (match && match[1]) {
    const possibleJson = match[1].trim();
    const parsed = tryParseJson(possibleJson);
    if (parsed !== null) {
      return parsed;
    }
  }
  
  return null;
};

/**
 * Extracts JSON from a larger text block, handling broken or incomplete JSON
 * @param {string} text - Text that might contain JSON
 * @returns {Object|null} Extracted JSON object or null if extraction fails
 */
export const extractJsonFromText = (text) => {
  if (!text) return null;
  
  // First try to find JSON in code blocks
  const codeBlockJson = extractJsonFromCodeBlocks(text);
  if (codeBlockJson) return codeBlockJson;
  
  // Try to reconstruct fragmented JSON
  // This approach is more robust for handling broken JSON fragments
  const jsonStart = text.indexOf('{');
  if (jsonStart === -1) return null;
  
  // Make sure we don't have partial JSON by looking for the complete object
  // Count braces to find matching closing brace
  let openBraces = 0;
  let jsonEnd = -1;
  
  for (let i = jsonStart; i < text.length; i++) {
    if (text[i] === '{') openBraces++;
    else if (text[i] === '}') {
      openBraces--;
      if (openBraces === 0) {
        jsonEnd = i + 1;
        break;
      }
    }
  }
  
  if (jsonEnd === -1) {
    // If we couldn't find a complete object, try our best to fix it
    return attemptJsonReconstruction(text, jsonStart);
  }
  
  const jsonStr = text.substring(jsonStart, jsonEnd);
  const parsed = tryParseJson(jsonStr);
  
  if (parsed) return parsed;
  
  // If direct parsing fails, try more aggressive approaches
  return attemptJsonReconstruction(text, jsonStart);
};

/**
 * Attempts to reconstruct broken JSON
 * @param {string} text - Text containing potentially broken JSON
 * @param {number} startIndex - Index where JSON starts
 * @returns {Object|null} Reconstructed JSON object or null
 */
const attemptJsonReconstruction = (text, startIndex) => {
  // Extract the text from the starting brace
  const substring = text.substring(startIndex);
  
  // Count total open and close braces to see if they balance
  let openBraces = 0;
  let closeBraces = 0;
  
  for (const char of substring) {
    if (char === '{') openBraces++;
    else if (char === '}') closeBraces++;
  }
  
  // If we have more open braces than close braces, try to fix by adding closing braces
  if (openBraces > closeBraces) {
    const missingBraces = openBraces - closeBraces;
    const fixedJson = substring + '}'.repeat(missingBraces);
    return tryParseJson(fixedJson);
  }
  
  // Try to manually extract the structure
  try {
    // Look for key JSON patterns
    const totalProjectMatch = /["']Total Project Fund["']\s*:\s*["']([^"']+)["']/i.exec(text);
    const ewsSummaryMatch = /["']EWS Summary["']\s*:\s*\{([^}]+)\}/i.exec(text);
    const ewsDetailedMatch = /["']EWS Detailed Analysis["']\s*:\s*\{([^}]+)\}/i.exec(text);
    const nonEwsMatch = /["']Non-EWS Summary["']\s*:\s*\{([^}]+)\}/i.exec(text);
    
    // Build a reconstructed object
    const reconstructed = {
      "Total Project Fund": totalProjectMatch ? totalProjectMatch[1] : "Unknown",
      "EWS Summary": {},
      "EWS Detailed Analysis": { "Included Outputs/Activities": [] },
      "Non-EWS Summary": { "Excluded Outputs": [] }
    };
    
    // Parse fragments if available
    if (ewsSummaryMatch) {
      try {
        const ewsSummaryObj = tryParseJson(`{${ewsSummaryMatch[1]}}`);
        if (ewsSummaryObj) reconstructed["EWS Summary"] = ewsSummaryObj;
      } catch (e) { /* Ignore parsing errors for fragments */ }
    }
    
    return reconstructed;
  } catch (e) {
    console.error("JSON reconstruction failed:", e);
    return null;
  }
};

/**
 * Parse a currency string to a number
 * @param {string} value - Currency string (e.g., "USD 1.2 Million")
 * @returns {number} Numeric value
 */
export const parseCurrencyToNumber = (value) => {
  if (!value || typeof value !== 'string') return 0;
  
  // Remove currency symbol, commas and extra spaces
  let numStr = value.replace(/[^\d.-]/g, '');
  
  // Parse numeric value
  let numericValue = parseFloat(numStr);
  
  // Handle millions, billions abbreviations
  if (value.toLowerCase().includes('million')) {
    numericValue *= 1000000;
  } else if (value.toLowerCase().includes('billion')) {
    numericValue *= 1000000000;
  }
  
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Formats currency values
 * @param {string|number} value - The currency value to format
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined) return 'N/A';
  
  // If value is a string that might contain currency symbols, parse it first
  const numericValue = typeof value === 'string' ? parseCurrencyToNumber(value) : value;
  
  if (isNaN(numericValue)) return 'N/A';
  
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: currency,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

/**
 * Formats percentage values
 * @param {number} value - The value to format as percentage
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return 'N/A';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return 'N/A';
  
  // If value is already in percentage (e.g., 42 for 42%), divide by 100
  const divisor = numValue > 1 ? 100 : 1;
  
  return new Intl.NumberFormat('en-US', { 
    style: 'percent', 
    minimumFractionDigits: 1, 
    maximumFractionDigits: 1,
  }).format(numValue / divisor);
};

/**
 * Extracts summary data from analysis results
 * @param {string|Object} resultsText - The full analysis results text or object
 * @returns {Object} Extracted summary data
 */
export const extractSummaryData = (resultsText) => {
  // Handle case where resultsText is already an object
  const jsonData = typeof resultsText === 'object' ? resultsText : extractJsonFromText(resultsText);
  
  if (!jsonData) {
    return {
      totalProjectFund: 'N/A',
      totalEwsAmount: 'N/A',
      totalNonEwsAmount: 'N/A',
      pillar1Amount: 'N/A',
      pillar2Amount: 'N/A',
      pillar3Amount: 'N/A',
      pillar4Amount: 'N/A',
      crossPillarAmount: 'N/A',
      ewsPercentage: 0,
      nonEwsPercentage: 0,
    };
  }

  // Extract amounts and parse them to numbers for calculations
  const totalProject = parseCurrencyToNumber(jsonData['Total Project Fund']);
  const totalEws = jsonData["EWS Summary"]?.["Total EWS Amount"] ? 
    parseCurrencyToNumber(jsonData["EWS Summary"]["Total EWS Amount"]) : 0;
  const totalNonEws = jsonData["Non-EWS Summary"]?.["Total Non-EWS Amount"] ? 
    parseCurrencyToNumber(jsonData["Non-EWS Summary"]["Total Non-EWS Amount"]) : 0;

  // Calculate percentages
  const ewsPercentage = totalProject ? (totalEws / totalProject) * 100 : 0;
  const nonEwsPercentage = totalProject ? (totalNonEws / totalProject) * 100 : 0;

  return {
    totalProjectFund: jsonData['Total Project Fund'] || 'N/A',
    totalEwsAmount: jsonData["EWS Summary"]?.["Total EWS Amount"] || 'N/A',
    totalNonEwsAmount: jsonData["Non-EWS Summary"]?.["Total Non-EWS Amount"] || 'N/A',
    pillar1Amount: jsonData["EWS Summary"]?.["Pillar 1 Amount"] || 'N/A',
    pillar2Amount: jsonData["EWS Summary"]?.["Pillar 2 Amount"] || 'N/A',
    pillar3Amount: jsonData["EWS Summary"]?.["Pillar 3 Amount"] || 'N/A',
    pillar4Amount: jsonData["EWS Summary"]?.["Pillar 4 Amount"] || 'N/A',
    crossPillarAmount: jsonData["EWS Summary"]?.["Cross Pillar Amount"] || 'N/A',
    ewsPercentage,
    nonEwsPercentage,
    ewsDetailed: jsonData["EWS Detailed Analysis"]?.["Included Outputs/Activities"] || [],
    nonEwsDetails: jsonData["Non-EWS Summary"]?.["Excluded Outputs"] || []
  };
};

/**
 * Groups EWS activities by pillar
 * @param {Array} activities - Array of EWS activities
 * @returns {Object} Activities grouped by pillar
 */
export const groupActivitiesByPillar = (activities) => {
  if (!activities || !Array.isArray(activities)) return {};
  
  return activities.reduce((acc, activity) => {
    const pillar = activity.Pillar || 'Unknown';
    if (!acc[pillar]) {
      acc[pillar] = [];
    }
    acc[pillar].push(activity);
    return acc;
  }, {});
};

/**
 * Calculates pillar distribution
 * @param {Object} summaryData - The summary data object
 * @returns {Array} Array of pillar distribution data for charting
 */
export const calculatePillarDistribution = (summaryData) => {
  const pillars = [
    { name: 'Pillar 1', amount: parseCurrencyToNumber(summaryData.pillar1Amount) },
    { name: 'Pillar 2', amount: parseCurrencyToNumber(summaryData.pillar2Amount) },
    { name: 'Pillar 3', amount: parseCurrencyToNumber(summaryData.pillar3Amount) },
    { name: 'Pillar 4', amount: parseCurrencyToNumber(summaryData.pillar4Amount) },
    { name: 'Cross Pillar', amount: parseCurrencyToNumber(summaryData.crossPillarAmount) }
  ];
  
  const totalEws = parseCurrencyToNumber(summaryData.totalEwsAmount);
  
  return pillars.map(pillar => ({
    ...pillar,
    percentage: totalEws ? (pillar.amount / totalEws) * 100 : 0
  })).filter(pillar => pillar.amount > 0);
};

/**
 * Generates color for a pillar
 * @param {string} pillarName - The name of the pillar
 * @returns {string} Hex color code
 */
export const getPillarColor = (pillarName) => {
  const colorMap = {
    'Pillar 1': '#4e79a7',
    'Pillar 2': '#f28e2c',
    'Pillar 3': '#e15759',
    'Pillar 4': '#76b7b2',
    'Cross Pillar': '#59a14f',
    'Non-EWS': '#edc949'
  };
  
  return colorMap[pillarName] || '#b07aa1'; // Default color
};

/**
 * Prepares data for a pie chart
 * @param {Object} summaryData - The summary data object
 * @returns {Array} Data for pie chart
 */
export const preparePieChartData = (summaryData) => {
  const pillars = calculatePillarDistribution(summaryData);
  
  return pillars.map(pillar => ({
    name: pillar.name,
    value: pillar.amount,
    color: getPillarColor(pillar.name)
  }));
};

/**
 * Prepares data for overall distribution chart
 * @param {Object} summaryData - The summary data object
 * @returns {Array} Data for distribution chart
 */
export const prepareOverallDistributionData = (summaryData) => {
  const ewsAmount = parseCurrencyToNumber(summaryData.totalEwsAmount);
  const nonEwsAmount = parseCurrencyToNumber(summaryData.totalNonEwsAmount);
  
  return [
    { name: 'EWS', value: ewsAmount, color: '#3366cc' },
    { name: 'Non-EWS', value: nonEwsAmount, color: '#dc3912' }
  ];
};

/**
 * Creates a detailed analysis object with all relevant data
 * @param {string|Object} resultsText - The full analysis results text or object
 * @returns {Object} Complete analysis object
 */
export const createDetailedAnalysis = (resultsText) => {
  const summaryData = extractSummaryData(resultsText);
  
  return {
    ...summaryData,
    pillarDistribution: calculatePillarDistribution(summaryData),
    pieChartData: preparePieChartData(summaryData),
    overallDistributionData: prepareOverallDistributionData(summaryData),
    groupedActivities: groupActivitiesByPillar(summaryData.ewsDetailed)
  };
};